import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, ShoppingCart, AlertCircle, CheckCircle2,
  RefreshCw, Receipt
} from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import { useAuth } from '../../context/AuthContext';
import ReceiptUploader from '../../components/budget/ReceiptUploader';
import ReceiptConfirmForm from '../../components/budget/ReceiptConfirmForm';
import MonthlySummary from '../../components/budget/MonthlySummary';
import DateSummary from '../../components/budget/DateSummary';
import StoreSummary from '../../components/budget/StoreSummary';
import ItemsList from '../../components/budget/ItemsList';
import '../../styles/budget.css';

const VIEWS = {
  SUMMARY: 'summary',
  UPLOAD:  'upload',
  CONFIRM: 'confirm',
  DATE:    'date',
  STORE:   'store',
  ITEMS:   'items',
};

const API_URL = process.env.REACT_APP_API_URL || 'https://api.synfin.ca';

const GroceriesPage = () => {
  const { user } = useAuth();
  const [view, setView]               = useState(VIEWS.SUMMARY);
  const [loading, setLoading]         = useState(true);
  const [toast, setToast]             = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);
  const [dateData, setDateData]       = useState([]);
  const [storeData, setStoreData]     = useState([]);
  const [itemsData, setItemsData]     = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedDay, setSelectedDay]     = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const [receiptResult, setReceiptResult] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // ── Fetch monthly summary ────────────────────────────────────
  const fetchMonthlyData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('expenses')
      .select('expense_date, total')
      .eq('user_id', user.id)
      .order('expense_date', { ascending: false });

    if (!error && data) {
      const months = {};
      data.forEach(exp => {
        const d = new Date(exp.expense_date + 'T12:00:00');
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        if (!months[key]) {
          months[key] = {
            month_key: key,
            month_name: d.toLocaleDateString('en-US', { month: 'long' }),
            year: d.getFullYear(),
            total: 0,
            visit_count: 0,
          };
        }
        months[key].total += parseFloat(exp.total) || 0;
        months[key].visit_count += 1;
      });
      const sorted = Object.values(months)
        .map(m => ({ ...m, total: Math.round(m.total * 100) / 100 }))
        .sort((a, b) => b.month_key.localeCompare(a.month_key));
      setMonthlyData(sorted);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchMonthlyData(); }, [fetchMonthlyData]);

  // ── Fetch date data ──────────────────────────────────────────
  const fetchDateData = async (month) => {
    const [year, mon] = month.month_key.split('-');
    const { data, error } = await supabase
      .from('expenses')
      .select('id, expense_date, store_name, total')
      .eq('user_id', user.id)
      .gte('expense_date', `${year}-${mon}-01`)
      .lte('expense_date', `${year}-${mon}-31`)
      .order('expense_date', { ascending: false });

    if (!error && data) {
      const days = {};
      data.forEach(exp => {
        const key = exp.expense_date;
        if (!days[key]) days[key] = { date: key, total: 0, stores: [], item_count: 0 };
        days[key].total += parseFloat(exp.total) || 0;
        if (!days[key].stores.includes(exp.store_name)) days[key].stores.push(exp.store_name);
      });
      const expenseIds = data.map(e => e.id);
      if (expenseIds.length > 0) {
        const { data: itemCounts } = await supabase
          .from('expense_items').select('expense_id').in('expense_id', expenseIds);
        if (itemCounts) {
          const countMap = itemCounts.reduce((acc, item) => {
            acc[item.expense_id] = (acc[item.expense_id] || 0) + 1;
            return acc;
          }, {});
          data.forEach(exp => {
            if (days[exp.expense_date]) days[exp.expense_date].item_count += countMap[exp.id] || 0;
          });
        }
      }
      setDateData(Object.values(days)
        .map(d => ({ ...d, total: Math.round(d.total * 100) / 100 }))
        .sort((a, b) => b.date.localeCompare(a.date)));
    }
  };

  // ── Fetch store data ─────────────────────────────────────────
  const fetchStoreData = async (day) => {
    const { data, error } = await supabase
      .from('expenses')
      .select('id, store_name, total, subtotal, tax')
      .eq('user_id', user.id)
      .eq('expense_date', day.date)
      .order('store_name');

    if (!error && data) {
      const enriched = await Promise.all(data.map(async (exp) => {
        const { count } = await supabase
          .from('expense_items')
          .select('*', { count: 'exact', head: true })
          .eq('expense_id', exp.id);
        return { ...exp, expense_id: exp.id, item_count: count || 0 };
      }));
      setStoreData(enriched);
    }
  };

  // ── Fetch items ───────────────────────────────────────────────
  const fetchItemsData = async (store) => {
    const { data, error } = await supabase
      .from('expense_items').select('*')
      .eq('expense_id', store.expense_id).order('category');
    if (!error && data) setItemsData(data);
  };

  // ── Navigation ────────────────────────────────────────────────
  const handleMonthClick = async (month) => {
    setSelectedMonth(month);
    await fetchDateData(month);
    setView(VIEWS.DATE);
  };

  const handleDateClick = async (day) => {
    setSelectedDay(day);
    await fetchStoreData(day);
    setView(VIEWS.STORE);
  };

  const handleStoreClick = async (store) => {
    setSelectedStore(store);
    await fetchItemsData(store);
    setView(VIEWS.ITEMS);
  };

  // ── Receipt flow ──────────────────────────────────────────────
  const handleReceiptProcessed = (result) => {
    setReceiptResult(result);
    setView(VIEWS.CONFIRM);
  };

  const handleSaveReceipt = async (data) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        showToast('Please log in again to save receipts.', 'error');
        return;
      }

      const formData = new FormData();
      if (data.imageFile) formData.append('receipt', data.imageFile);
      formData.append('store_name', data.store_name);
      formData.append('expense_date', data.expense_date);
      formData.append('subtotal', data.subtotal || 0);
      formData.append('tax', data.tax || 0);
      formData.append('total', data.total);
      formData.append('currency', data.currency || 'CAD');
      formData.append('category', data.category || 'Groceries');
      formData.append('confidence', data.confidence || 0);
      formData.append('items', JSON.stringify(data.items || []));

      // New payment and savings fields
      formData.append('total_savings', data.total_savings || 0);
      formData.append('payment_method', data.payment_method || 'Unknown');
      if (data.payment_card_type) formData.append('payment_card_type', data.payment_card_type);
      if (data.payment_card_last4) formData.append('payment_card_last4', data.payment_card_last4);
      if (data.cash_tendered) formData.append('cash_tendered', data.cash_tendered);
      if (data.change_due) formData.append('change_due', data.change_due);
      if (data.rounded_total) formData.append('rounded_total', data.rounded_total);

      const response = await fetch(`${API_URL}/api/receipts/save`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: formData,
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.message || 'Failed to save');

      showToast(`Receipt saved! $${parseFloat(data.total).toFixed(2)} at ${data.store_name}`);
      setView(VIEWS.SUMMARY);
      setReceiptResult(null);
      await fetchMonthlyData();

    } catch (err) {
      console.error('Save error:', err);
      showToast('Failed to save receipt. Please try again.', 'error');
    }
  };

  // ── Render ────────────────────────────────────────────────────
  return (
    <div className="budget-page">
      {toast && (
        <div className={`budget-toast budget-toast-${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          {toast.message}
        </div>
      )}

      {view === VIEWS.SUMMARY && (
        <div className="budget-header">
          <div>
            <h1 className="budget-title"><ShoppingCart size={22} /> Groceries</h1>
            <p className="budget-subtitle">Track your grocery spending by month, date and store</p>
          </div>
          <div className="budget-header-actions">
            <button className="budget-refresh-btn" onClick={fetchMonthlyData}>
              <RefreshCw size={15} className={loading ? 'spinning' : ''} />
            </button>
            <button className="budget-upload-btn" onClick={() => setView(VIEWS.UPLOAD)}>
              <Plus size={18} /><Receipt size={16} /> Add Receipt
            </button>
          </div>
        </div>
      )}

      {view === VIEWS.SUMMARY && (
        loading ? (
          <div className="budget-loading">
            <div className="budget-spinner" />
            <p>Loading your expenses...</p>
          </div>
        ) : (
          <MonthlySummary data={monthlyData} onMonthClick={handleMonthClick} />
        )
      )}

      {view === VIEWS.UPLOAD && (
        <ReceiptUploader
          onReceiptProcessed={handleReceiptProcessed}
          onCancel={() => setView(VIEWS.SUMMARY)}
        />
      )}

      {view === VIEWS.CONFIRM && receiptResult && (
        <ReceiptConfirmForm
          receiptData={receiptResult}
          onSave={handleSaveReceipt}
          onBack={() => setView(VIEWS.UPLOAD)}
        />
      )}

      {view === VIEWS.DATE && selectedMonth && (
        <DateSummary
          month={selectedMonth} data={dateData}
          onDateClick={handleDateClick} onBack={() => setView(VIEWS.SUMMARY)}
        />
      )}

      {view === VIEWS.STORE && selectedDay && (
        <StoreSummary
          day={selectedDay} data={storeData}
          onStoreClick={handleStoreClick} onBack={() => setView(VIEWS.DATE)}
        />
      )}

      {view === VIEWS.ITEMS && selectedStore && (
        <ItemsList
          store={selectedStore} items={itemsData}
          onBack={() => setView(VIEWS.STORE)}
        />
      )}
    </div>
  );
};

export default GroceriesPage;
